import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, inject, Renderer2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CanDeactivateState } from './services/guards/can-deactivate.guard';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { InjectorService } from './services/injector.service';
import { MAX_MOBILE_WIDTH, ScreenService } from './services/screen-service.service';
import { BaseComponent } from './models/base/base-component';
import { fromEvent } from 'rxjs';
import { SessionService } from './services/session-service';
import { PortalService } from './services/portal/portal.service';
import { Meta, Title } from '@angular/platform-browser';
import { PortalType } from './models/enum/shared/portal-type';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent extends BaseComponent {
  private mobileNavScrollLockClass = 'mobile-nav-scroll-locked';
  constructor(
    private router: Router,
    // @ts-ignore
    private injectorService: InjectorService, // Do not remove, or else injector service does not work.
    private sessionService: SessionService,
    private titleService: Title,
    private portalService: PortalService,
    private el: ElementRef,
    private meta: Meta
  ) {
    super();

    // Config for Abandon changes on back button
    window.onpopstate = () => (CanDeactivateState.defendAgainstBrowserBackButton = true);
    router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        tap(() => (CanDeactivateState.defendAgainstBrowserBackButton = false))
      )
      .subscribe();

    // remove robots meta tag in production
    if (environment.production) {
      this.meta.removeTag('name="robots"');
    }
  }

  private screenService = inject(ScreenService);
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);

  public showOverlay$ = this.screenService.showOverlay$;

  setupViews() {
    this.onResize(window?.innerWidth);
  }

  setupBindings() {
    this.router.events.subscribe(evt => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    });

    fromEvent(window, 'resize').subscribeWhileAlive({
      owner: this,
      next: event => {
        const target = event.target as Window;
        this.onResize(target.innerWidth);
      }
    });

    fromEvent<StorageEvent>(window, 'storage').subscribeWhileAlive({
      owner: this,
      next: (event: StorageEvent) => {
        if (event.key === 'SessionContainer' && event.newValue === null) {
          this.sessionService.destroySession.next(true);
          this.router.navigate(['/auth/sign-in']).then();
        }
      }
    });

    this.portalService.portalType$.subscribeWhileAlive({
      owner: this,
      next: portalType => {
        switch (portalType) {
          case PortalType.Member:
            this.titleService.setTitle($localize`SPP Member`);
            break;
          case PortalType.Employer:
            this.titleService.setTitle($localize`SPP Employer`);
            break;
          case PortalType.Internal:
            this.titleService.setTitle($localize`SPP Internal`);
            break;
          default:
            this.titleService.setTitle($localize`SPP Member`);
        }
      }
    });

    this.screenService.mobileNavScrollLocked$.pipe(distinctUntilChanged()).subscribeWhileAlive({
      owner: this,
      next: isScrollLocked => {
        this.toggleMobileNavScrollLock(isScrollLocked);
      }
    });
  }

  private onResize(windowInnerWidth: number) {
    if (windowInnerWidth > 0) {
      this.screenService.setWidth(windowInnerWidth);
      this.screenService.setIsTabletOrSmaller(windowInnerWidth <= ScreenService.TRUE_TABLET_WIDTH);
      this.screenService.setIsMobile(windowInnerWidth <= MAX_MOBILE_WIDTH);
    }
  }

  private toggleMobileNavScrollLock(isScrollLocked: boolean): void {
    [this.document.documentElement, this.document.body].forEach(element => {
      if (isScrollLocked) {
        this.renderer.addClass(element, this.mobileNavScrollLockClass);
      } else {
        this.renderer.removeClass(element, this.mobileNavScrollLockClass);
      }
    });
  }
}
